import React, { useState, useMemo } from 'react'
import { updateCandidate, uploadRouteFile, fetchTickets, deleteTicket, updateTicket, updateAgent, fetchCandidateById, fetchAgentById } from '../lib/api'
import TicketModal from './TicketModal'

const WEEKDAYS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс']
const MONTHS = ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь']

function fmtTime(date, time) {
  if (!date || !time) return ''
  try {
    const [y, m, d] = date.split('-').map(Number)
    const [h, min] = time.split(':').map(Number)
    const dt = new Date(Date.UTC(y, m - 1, d, h, min))
    return new Intl.DateTimeFormat('ru-RU', { timeZone: 'UTC', hour: '2-digit', minute: '2-digit' }).format(dt)
  } catch (e) { return time }
}

function fmtMsk(date, time, fromTz) {
  if (!date || !time) return ''
  try {
    const [y, m, d] = date.split('-').map(Number)
    const [h, min] = time.split(':').map(Number)
    const localDt = new Date(Date.UTC(y, m - 1, d, h, min))
    const localOffset = new Intl.DateTimeFormat('en', { timeZone: fromTz, timeZoneName: 'shortOffset' }).formatToParts(localDt).find(p => p.type === 'timeZoneName')?.value || 'UTC+0'
    const mskOffset = new Intl.DateTimeFormat('en', { timeZone: 'Europe/Moscow', timeZoneName: 'shortOffset' }).formatToParts(localDt).find(p => p.type === 'timeZoneName')?.value || 'UTC+3'
    const parseOffset = (s) => { const m = s.match(/UTC([+-])(\d+)(?::(\d+))?/); if (!m) return 0; return (m[1] === '-' ? -1 : 1) * (parseInt(m[2]) * 60 + (parseInt(m[3]) || 0)) }
    const diffMin = parseOffset(mskOffset) - parseOffset(localOffset)
    const mskDt = new Date(localDt.getTime() + diffMin * 60000)
    return new Intl.DateTimeFormat('ru-RU', { timeZone: 'UTC', hour: '2-digit', minute: '2-digit' }).format(mskDt)
  } catch (e) { return '' }
}

function BookingModal({ date, candidates, locations, onClose, onSave }) {
  const [form, setForm] = useState({ candidate_id: '', hotel_name: '', location_id: '', check_in: date || '', check_out: '', price: '', notes: '' })
  const [search, setSearch] = useState('')
  const [showDropdown, setShowDropdown] = useState(false)
  const [file, setFile] = useState(null)
  const [saving, setSaving] = useState(false)

  function set(field, value) { setForm(f => ({ ...f, [field]: value })) }

  async function handleSubmit(e) {
    e.preventDefault()
    if (!form.candidate_id || !form.check_in || !form.check_out) return
    setSaving(true)
    try {
      let fileData = null
      if (file) {
        fileData = await uploadRouteFile(file, 'hotel_' + form.candidate_id + '_' + Date.now(), 'booking')
      }
      await onSave({ ...form, file: fileData })
    } catch (err) { alert('Ошибка: ' + err.message) }
    setSaving(false)
  }

const filteredCandidates = candidates.filter(c => {
    if (c.archived) return false
    if (!search) return true
    const q = search.toLowerCase()
    return c.name?.toLowerCase().includes(q) || c.passport?.toLowerCase().includes(q)
  })

  const selectedCandidate = candidates.find(c => c.id === form.candidate_id)

  return (
    <div className="modal-backdrop">
      <div className="modal modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>🏨 Новое бронирование</h2>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={handleSubmit} className="modal-form">
          <label>Кандидат *</label>
          <div style={{ position: 'relative', marginBottom: 12 }}>
            <input value={selectedCandidate ? selectedCandidate.name : search} onChange={e => { setSearch(e.target.value); set('candidate_id', ''); setShowDropdown(true) }} onFocus={() => setShowDropdown(true)} placeholder="Поиск по имени или паспорту..." style={{ width: '100%', fontSize: 13, padding: '6px 10px', borderRadius: 8, border: '1px solid #e0e0dc' }} />
            {showDropdown && filteredCandidates.length > 0 && (
              <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'white', border: '1px solid #e0e0dc', borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.1)', zIndex: 100, maxHeight: 200, overflowY: 'auto', marginTop: 2 }}>
                {filteredCandidates.map(c => (
                  <div key={c.id} onMouseDown={() => { set('candidate_id', c.id); setSearch(''); setShowDropdown(false) }} style={{ padding: '8px 12px', fontSize: 13, cursor: 'pointer', borderBottom: '0.5px solid #f5f5f3' }} onMouseOver={e => e.target.style.background = '#f5f5f3'} onMouseOut={e => e.target.style.background = 'transparent'}>
                    {c.name} <span style={{ color: '#888', fontSize: 11 }}>{c.passport}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          <label>Название и адрес отеля
            <input value={form.hotel_name} onChange={e => set('hotel_name', e.target.value)} placeholder="Название отеля, адрес" />
          </label>
          <label>Локация
            <select value={form.location_id} onChange={e => set('location_id', e.target.value)}>
              <option value="">— выберите локацию —</option>
              {locations.map(l => <option key={l.id} value={l.id}>{l.icon} {l.name}</option>)}
            </select>
          </label>
          <div className="row-2">
            <label>Дата заезда *
              <input type="date" value={form.check_in} onChange={e => set('check_in', e.target.value)} required />
            </label>
            <label>Дата выезда *
              <input type="date" value={form.check_out} onChange={e => set('check_out', e.target.value)} required />
            </label>
          </div>
          <label>Стоимость (₽)
            <input type="number" value={form.price} onChange={e => set('price', e.target.value)} placeholder="0" />
          </label>
          <label>Примечание
            <input value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Номер брони, условия..." />
          </label>
          <label style={{ display: 'inline-block', fontSize: 12, padding: '6px 12px', border: '1px dashed #d0d0cc', borderRadius: 7, cursor: 'pointer', color: '#888', marginBottom: 8 }}>
            📎 {file ? file.name : 'Прикрепить подтверждение'}
            <input type="file" accept="image/*,.pdf" onChange={e => setFile(e.target.files[0])} style={{ display: 'none' }} />
          </label>
          <div className="modal-actions">
            <button type="button" className="btn btn-outline" onClick={onClose}>Отмена</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Сохранение...' : 'Добавить'}</button>
          </div>
        </form>
      </div>
    </div>
  )
}
function FlightCardModal({ ticketId, candidate, onClose, onOpenCandidate, onDeleted, onUpdated, isAdmin }) {
  const [ticket, setTicket] = useState(null)
  const [loading, setLoading] = useState(true)
const [editing, setEditing] = useState(false)
  const [editForm, setEditForm] = useState(null)
  const [editFile, setEditFile] = useState(null)

  React.useEffect(() => {
    fetchTickets().then(tickets => {
      setTicket(tickets.find(t => t.id === ticketId) || null)
      setLoading(false)
    }).catch(() => setLoading(false))
  }, [ticketId])

  return (
    <div className="modal-backdrop">
      <div className="modal modal-sm" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {ticket?.type === 'автобус' ? '🚌 Билет' : '✈ Билет'}
            {ticket?.type === 'обратный' && <span style={{ fontSize: 12, fontWeight: 600, padding: '2px 8px', borderRadius: 10, background: '#dbeafe', color: '#1d4ed8' }}>обратный</span>}
          </h2>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>
        <div className="detail-body">
          {loading && <div style={{ textAlign: 'center', padding: 20, color: '#888' }}>Загрузка...</div>}
          {!loading && !ticket && <div style={{ textAlign: 'center', padding: 20, color: '#bbb' }}>Билет не найден</div>}
{ticket && !editing && (
            <>
              <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 10 }}>{candidate.name}</div>
              <table className="detail-table">
                <tbody>
                  <tr><td>Маршрут</td><td>{ticket.from_city} → {ticket.to_city}</td></tr>
                  <tr><td>Вылет/выезд</td><td>{ticket.departure_date ? String(ticket.departure_date).slice(0,10).split('-').reverse().join('.') : '—'}{ticket.departure_time ? ' ' + ticket.departure_time.slice(0, 5) + ' (местное)' : ''}</td></tr>
                  {ticket.arrival_date && <tr><td>Прибытие</td><td>{String(ticket.arrival_date).slice(0,10).split('-').reverse().join('.')}{ticket.arrival_time ? ' ' + ticket.arrival_time.slice(0, 5) + ' (местное)' : ''}</td></tr>}
                  {ticket.ticket_number && <tr><td>Номер билета</td><td>{ticket.ticket_number}</td></tr>}
                  {ticket.price > 0 && <tr><td>Стоимость</td><td>{parseFloat(ticket.price).toLocaleString('ru-RU')} ₽</td></tr>}
                  {ticket.purchased_by && <tr><td>Купил</td><td>{ticket.purchased_by}</td></tr>}
                  {ticket.airline && <tr><td>Авиакомпания</td><td>{ticket.airline}</td></tr>}
                  {ticket.aggregator && <tr><td>Агрегатор</td><td>{ticket.aggregator}</td></tr>}
                  {ticket.payment_method && <tr><td>Платёжное средство</td><td>{ticket.payment_method}</td></tr>}
                  <tr><td>Статус билета</td><td>{ticket.refund_status === 'Возврат' ? '🔴 Возврат' : ticket.refund_status === 'Успех' ? '🟢 Успех' : '🟡 В процессе'}</td></tr>
                  {ticket.refund_amount && <tr><td>Сумма возврата</td><td>{parseFloat(ticket.refund_amount).toLocaleString('ru-RU')} ₽</td></tr>}
                </tbody>
              </table>
              {ticket.file && (
                <div style={{ marginTop: 10 }}>
                  <a href={ticket.file.url} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: '#185FA5', textDecoration: 'none' }}>📄 {ticket.file.name}</a>
                </div>
              )}
            </>
          )}
          {ticket && editing && editForm && (
            <div>
              <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 4 }}>Откуда</label>
              <input value={editForm.from_city || ''} onChange={e => setEditForm(f => ({ ...f, from_city: e.target.value }))} style={{ width: '100%', fontSize: 13, padding: '6px 10px', borderRadius: 8, border: '1px solid #e0e0dc', marginBottom: 8 }} />
              <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 4 }}>Куда</label>
              <input value={editForm.to_city || ''} onChange={e => setEditForm(f => ({ ...f, to_city: e.target.value }))} style={{ width: '100%', fontSize: 13, padding: '6px 10px', borderRadius: 8, border: '1px solid #e0e0dc', marginBottom: 8 }} />
              <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 4 }}>Дата вылета/выезда</label>
              <input type="date" value={editForm.departure_date || ''} onChange={e => setEditForm(f => ({ ...f, departure_date: e.target.value }))} style={{ width: '100%', fontSize: 13, padding: '6px 10px', borderRadius: 8, border: '1px solid #e0e0dc', marginBottom: 8 }} />
              <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 4 }}>Время вылета/выезда</label>
              <input type="time" value={editForm.departure_time || ''} onChange={e => setEditForm(f => ({ ...f, departure_time: e.target.value }))} style={{ width: '100%', fontSize: 13, padding: '6px 10px', borderRadius: 8, border: '1px solid #e0e0dc', marginBottom: 8 }} />
              <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 4 }}>Дата прибытия</label>
              <input type="date" value={editForm.arrival_date || ''} onChange={e => setEditForm(f => ({ ...f, arrival_date: e.target.value }))} style={{ width: '100%', fontSize: 13, padding: '6px 10px', borderRadius: 8, border: '1px solid #e0e0dc', marginBottom: 8 }} />
              <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 4 }}>Время прибытия</label>
              <input type="time" value={editForm.arrival_time || ''} onChange={e => setEditForm(f => ({ ...f, arrival_time: e.target.value }))} style={{ width: '100%', fontSize: 13, padding: '6px 10px', borderRadius: 8, border: '1px solid #e0e0dc', marginBottom: 8 }} />
              <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 4 }}>Номер билета</label>
              <input value={editForm.ticket_number || ''} onChange={e => setEditForm(f => ({ ...f, ticket_number: e.target.value }))} style={{ width: '100%', fontSize: 13, padding: '6px 10px', borderRadius: 8, border: '1px solid #e0e0dc', marginBottom: 8 }} />
              <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 4 }}>Стоимость (₽)</label>
              <input type="number" value={editForm.price || ''} onChange={e => setEditForm(f => ({ ...f, price: e.target.value }))} style={{ width: '100%', fontSize: 13, padding: '6px 10px', borderRadius: 8, border: '1px solid #e0e0dc', marginBottom: 8 }} />
              {ticket?.file && (
                <div style={{ marginBottom: 6 }}>
                  <span style={{ fontSize: 11, color: '#888' }}>Текущий файл: </span>
                  <a href={ticket.file.url} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: '#185FA5', textDecoration: 'none' }}>📄 {ticket.file.name}</a>
                </div>
              )}
              <label style={{ display: 'inline-block', fontSize: 12, padding: '6px 12px', border: '1px dashed #d0d0cc', borderRadius: 7, cursor: 'pointer', color: '#888' }}>
                📎 {editFile ? editFile.name : (ticket?.file ? 'Заменить файл билета' : 'Прикрепить файл билета')}
                <input type="file" accept="image/*,.pdf" onChange={e => setEditFile(e.target.files[0])} style={{ display: 'none' }} />
              </label>
            </div>
          )}
        </div>
        <div className="modal-actions" style={{ flexWrap: 'wrap', gap: 6 }}>
          {!editing && ticket && isAdmin && (
            <button className="btn btn-danger" onClick={async () => {
              if (!window.confirm('Удалить билет? Соответствующая точка маршрута также будет удалена.')) return
              try {
                await deleteTicket(ticket.id)
                const freshC = await fetchCandidateById(candidate.id)
                const updatedRoute = (freshC.route || []).filter(p => p.ticket_id !== ticket.id)
                const updatedExpenses = (freshC.expenses || []).filter(e => e.ticket_id !== ticket.id)
                await updateCandidate(candidate.id, { route: updatedRoute, expenses: updatedExpenses })
                onUpdated && onUpdated(candidate.id, { route: updatedRoute, expenses: updatedExpenses })
                onClose()
              } catch (e) { alert('Ошибка: ' + e.message) }
            }}>Удалить</button>
          )}
          {!editing && ticket && (
            <button className="btn btn-outline" onClick={() => { setEditForm({ ...ticket }); setEditing(true) }}>Редактировать</button>
          )}
          {editing && (
            <button className="btn btn-outline" onClick={() => setEditing(false)}>Отмена</button>
          )}
          {editing && (
            <button className="btn btn-primary" onClick={async () => {
              try {
                let newFileData = ticket.file
                if (editFile) {
                  newFileData = await uploadRouteFile(editFile, 'ticket_' + ticket.id + '_' + Date.now(), 'ticket')
                }
                await updateTicket(ticket.id, {
                  from_city: editForm.from_city, to_city: editForm.to_city,
                  departure_date: editForm.departure_date, departure_time: editForm.departure_time,
                  arrival_date: editForm.arrival_date, arrival_time: editForm.arrival_time,
                  ticket_number: editForm.ticket_number, price: editForm.price,
                  file: newFileData
                })
                const freshC2 = await fetchCandidateById(candidate.id)
                const updatedRoute = (freshC2.route || []).map(p => p.ticket_id === ticket.id ? {
                  ...p,
                  from: editForm.from_city, to: editForm.to_city,
                  from_date: editForm.departure_date, from_time: editForm.departure_time,
                  to_date: editForm.arrival_date || editForm.departure_date, to_time: editForm.arrival_time,
                  files: editFile ? [...(p.files || []).filter(f => f.url !== ticket.file?.url), newFileData] : (p.files || [])
                } : p)
                await updateCandidate(candidate.id, { route: updatedRoute })
                onUpdated && onUpdated(candidate.id, { route: updatedRoute })
                setTicket({ ...ticket, ...editForm, file: newFileData })
                setEditFile(null)
                setEditing(false)
              } catch (e) { alert('Ошибка: ' + e.message) }
            }}>Сохранить</button>
          )}
          {!editing && <button className="btn btn-outline" onClick={onClose}>Закрыть</button>}
          {!editing && <button className="btn btn-primary" onClick={onOpenCandidate}>Открыть карточку кандидата</button>}
        </div>
      </div>
    </div>
  )
}
export default function CalendarView({ candidates, agents = [], locations, onClickCandidate, onClickFromFlight, onClickAgent, currentUserId, currentUserName, isAdmin }) {
  const now = new Date()
  const [year, setYear] = useState(now.getFullYear())
  const [month, setMonth] = useState(now.getMonth())
  const [search, setSearch] = useState('')
  const [tooltip, setTooltip] = useState(null)
  const [expandedDay, setExpandedDay] = useState(null)
  const [calTab, setCalTab] = useState('flights')
  const [flightTypeFilter, setFlightTypeFilter] = useState('all')
  const [bookingModal, setBookingModal] = useState(null)
const [ticketModal, setTicketModal] = useState(false)
const [flightCardModal, setFlightCardModal] = useState(null)
const [editBookingModal, setEditBookingModal] = useState(null)
const [editingBooking, setEditingBooking] = useState(false)
const [editForm, setEditForm] = useState(null)
  const [localCandidates, setLocalCandidates] = useState(candidates)
  const [localAgents, setLocalAgents] = useState(agents)

  React.useEffect(() => { setLocalCandidates(candidates) }, [candidates])
  React.useEffect(() => { setLocalAgents(agents) }, [agents])

  function prevMonth() { if (month === 0) { setMonth(11); setYear(y => y - 1) } else setMonth(m => m - 1) }
  function nextMonth() { if (month === 11) { setMonth(0); setYear(y => y + 1) } else setMonth(m => m + 1) }
  function goToday() { setYear(now.getFullYear()); setMonth(now.getMonth()) }

  const flights = useMemo(() => {
    const result = []
    localCandidates.forEach(c => {
      if (c.archived) return
      const route = Array.isArray(c.route) ? c.route : []
      route.forEach((point, idx) => {
        const tt = point.transport_type || 'авиа'
        if (point.from_date) result.push({ candidate: c, point, idx, date: point.from_date, time: point.from_time, type: 'dep', city: point.from, toCity: point.to, tz: point.from_tz || 'Europe/Moscow', transport_type: tt })
        if (point.to_date) result.push({ candidate: c, point, idx, date: point.to_date, time: point.to_time, type: 'arr', city: point.to, fromCity: point.from, tz: point.to_tz || 'Europe/Moscow', transport_type: tt })
      })
    })
    localAgents.forEach(a => {
      const route = Array.isArray(a.route) ? a.route : []
      const agentCard = { id: a.id, name: a.name, _isAgent: true }
      route.forEach((point, idx) => {
        const tt = point.transport_type || 'авиа'
        if (point.from_date) result.push({ candidate: agentCard, point, idx, date: point.from_date, time: point.from_time, type: 'dep', city: point.from, toCity: point.to, tz: point.from_tz || 'Europe/Moscow', _isAgent: true, transport_type: tt })
        if (point.to_date) result.push({ candidate: agentCard, point, idx, date: point.to_date, time: point.to_time, type: 'arr', city: point.to, fromCity: point.from, tz: point.to_tz || 'Europe/Moscow', _isAgent: true, transport_type: tt })
      })
    })
    return result
  }, [localCandidates, localAgents])

  const bookings = useMemo(() => {
    const result = []
    localCandidates.forEach(c => {
      if (c.archived) return
      const hb = Array.isArray(c.hotel_bookings) ? c.hotel_bookings : []
      hb.forEach(b => { result.push({ ...b, hotel_name: b.hotel_name || b.hotel || '—', candidate: c, _isAgent: false }) })
    })
    localAgents.forEach(a => {
      const hb = Array.isArray(a.hotel_bookings) ? a.hotel_bookings : []
      hb.forEach((b, idx) => {
        result.push({
          hotel_name: b.hotel || b.hotel_name || '—',
          check_in: b.check_in,
          check_out: b.check_out,
          price: b.price,
          notes: b.notes,
          file: b.file,
          _agentId: a.id,
          _bookingIdx: idx,
          candidate: { id: a.id, name: a.name, _isAgent: true },
          _isAgent: true,
        })
      })
    })
    return result
  }, [localCandidates, localAgents])

  const filteredFlights = useMemo(() => {
    let result = flights
    if (flightTypeFilter !== 'all') result = result.filter(f => (f.transport_type || 'авиа') === flightTypeFilter)
    if (search) { const q = search.toLowerCase(); result = result.filter(f => f.candidate.name?.toLowerCase().includes(q) || f.city?.toLowerCase().includes(q) || f.toCity?.toLowerCase().includes(q)) }
    return result
  }, [flights, search, flightTypeFilter])

  const firstDay = new Date(year, month, 1)
  const lastDay = new Date(year, month + 1, 0)
  const startDow = (firstDay.getDay() + 6) % 7
  const totalDays = lastDay.getDate()
  const cells = []
  for (let i = 0; i < startDow; i++) cells.push(null)
  for (let d = 1; d <= totalDays; d++) cells.push(d)
  while (cells.length % 7 !== 0) cells.push(null)

  function flightsForDay(day) {
    if (!day) return []
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`
    return filteredFlights.filter(f => f.date === dateStr)
  }

  function bookingsForDay(day) {
    if (!day) return []
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`
    return bookings.filter(b => b.check_in <= dateStr && b.check_out >= dateStr)
  }

  function isCheckIn(b, day) {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`
    return b.check_in === dateStr
  }

  const todayDay = now.getFullYear() === year && now.getMonth() === month ? now.getDate() : null

  function shortName(name) {
    if (!name) return ''
    const parts = name.split(' ')
    if (parts.length >= 2) return parts[0][0] + '. ' + parts[1]
    return name
  }

async function handleSaveBooking(form) {
    const candidate = localCandidates.find(c => c.id === form.candidate_id)
    if (!candidate) return
    const locationName = locations?.find(l => l.id === form.location_id)?.name || ''
    const newBooking = {
      hotel_name: form.hotel_name,
      location_id: form.location_id,
      location_name: locationName,
      check_in: form.check_in,
      check_out: form.check_out,
      price: form.price,
      notes: form.notes,
      file: form.file,
      created_at: new Date().toISOString()
    }
    const freshC = await fetchCandidateById(candidate.id)
    const updatedBookings = [...(freshC.hotel_bookings || []), newBooking]
    const updates = { hotel_bookings: updatedBookings }
    if (form.file && Array.isArray(freshC.route)) {
      const loc = locations?.find(l => l.id === form.location_id)
      const routePointIdx = freshC.route.findIndex(p => p.to === loc?.name || p.from === loc?.name)
      if (routePointIdx !== -1) {
        const updatedRoute = freshC.route.map((p, i) => i === routePointIdx ? { ...p, files: [...(p.files || []), form.file] } : p)
        updates.route = updatedRoute
      }
    }
    await updateCandidate(candidate.id, updates)
    setLocalCandidates(prev => prev.map(c => c.id === candidate.id ? { ...c, ...updates } : c))
    setBookingModal(null)
  }

  return (
    <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e8e8e5', overflow: 'hidden' }}>
      <div style={{ padding: '12px 16px', borderBottom: '1px solid #f0f0ec', display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button onClick={prevMonth} style={{ background: 'transparent', border: '1px solid #e0e0dc', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: 14 }}>‹</button>
            <button onClick={goToday} style={{ background: 'transparent', border: '1px solid #e0e0dc', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: 12 }}>Сегодня</button>
            <button onClick={nextMonth} style={{ background: 'transparent', border: '1px solid #e0e0dc', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: 14 }}>›</button>
            <span style={{ fontSize: 15, fontWeight: 600 }}>{MONTHS[month]} <span style={{ color: '#888' }}>{year}</span></span>
          </div>
          <div style={{ display: 'flex', background: '#f5f5f3', borderRadius: 8, padding: 3, gap: 3, flexShrink: 0 }}>
            <button onClick={() => setCalTab('flights')} style={{ fontSize: 12, padding: '5px 10px', borderRadius: 6, border: 'none', cursor: 'pointer', background: calTab === 'flights' ? 'white' : 'transparent', fontWeight: calTab === 'flights' ? 600 : 400, color: calTab === 'flights' ? '#1a1a1a' : '#888', boxShadow: calTab === 'flights' ? '0 1px 4px rgba(0,0,0,0.1)' : 'none', whiteSpace: 'nowrap' }}>✈ Перелёты</button>
            <button onClick={() => setCalTab('hotels')} style={{ fontSize: 12, padding: '5px 10px', borderRadius: 6, border: 'none', cursor: 'pointer', background: calTab === 'hotels' ? 'white' : 'transparent', fontWeight: calTab === 'hotels' ? 600 : 400, color: calTab === 'hotels' ? '#1a1a1a' : '#888', boxShadow: calTab === 'hotels' ? '0 1px 4px rgba(0,0,0,0.1)' : 'none', whiteSpace: 'nowrap' }}>🏨 Отели</button>
          </div>
        </div>
        {calTab === 'flights' && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="🔍 Кандидат или направление" style={{ fontSize: 13, padding: '6px 14px', borderRadius: 20, border: '1px solid #e0e0dc', background: '#fafaf8', flex: 1, boxSizing: 'border-box' }} />
            <div style={{ display: 'flex', background: '#f5f5f3', borderRadius: 8, padding: 3, gap: 2, flexShrink: 0 }}>
              {[['all', 'Все'], ['авиа', '✈'], ['автобус', '🚌']].map(([val, label]) => (
                <button key={val} onClick={() => setFlightTypeFilter(val)} style={{ fontSize: 12, padding: '4px 8px', borderRadius: 6, border: 'none', cursor: 'pointer', background: flightTypeFilter === val ? 'white' : 'transparent', fontWeight: flightTypeFilter === val ? 600 : 400, color: flightTypeFilter === val ? '#1a1a1a' : '#888', boxShadow: flightTypeFilter === val ? '0 1px 4px rgba(0,0,0,0.1)' : 'none' }}>{label}</button>
              ))}
            </div>
          </div>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', borderBottom: '1px solid #f0f0ec' }}>
        {WEEKDAYS.map(d => <div key={d} style={{ padding: '8px 4px', fontSize: 11, fontWeight: 600, color: '#888', textAlign: 'center', overflow: 'hidden' }}>{d}</div>)}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))' }}>
        {cells.map((day, idx) => {
          const dayFlights = calTab === 'flights' ? flightsForDay(day) : []
          const dayBookings = calTab === 'hotels' ? bookingsForDay(day) : []
          const isToday = day === todayDay
          const isWeekend = idx % 7 >= 5
          const dateStr = day ? `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}` : ''

          return (
            <div key={idx} style={{ minHeight: 80, padding: '4px', borderRight: idx % 7 !== 6 ? '1px solid #f5f5f3' : 'none', borderBottom: '1px solid #f5f5f3', background: day ? 'white' : '#fafaf8', overflow: 'hidden', minWidth: 0 }}>
              {day && (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 3 }}>
                    <div style={{ fontSize: 12, fontWeight: isToday ? 700 : 400, color: isToday ? 'white' : isWeekend ? '#E24B4A' : '#1a1a1a', background: isToday ? '#1a1a1a' : 'transparent', width: 22, height: 22, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{day}</div>
                    {calTab === 'hotels' && <button onClick={() => setBookingModal({ date: dateStr })} style={{ fontSize: 10, color: '#bbb', background: 'transparent', border: 'none', cursor: 'pointer', padding: '0 2px', flexShrink: 0 }}>+</button>}
                  </div>

                  {calTab === 'flights' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {dayFlights.slice(0, 3).map((f, i) => (
                        <div key={i} style={{ fontSize: 10, padding: '2px 3px', borderRadius: 3, background: f._isAgent ? (f.type === 'dep' ? '#FEF3E2' : '#F0EAF8') : (f.type === 'dep' ? '#E6F1FB' : '#EAF3DE'), color: f._isAgent ? (f.type === 'dep' ? '#7A4500' : '#4A2080') : (f.type === 'dep' ? '#0C447C' : '#27500A'), cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 2, minWidth: 0, overflow: 'hidden' }}
                          onMouseEnter={e => { const rect = e.currentTarget.getBoundingClientRect(); setTooltip({ flight: f, x: rect.left, y: rect.bottom + 4 }) }}
                          onMouseLeave={() => setTooltip(null)}
                          onClick={() => { if (f._isAgent) { onClickAgent && onClickAgent() } else if (f.point.ticket_id) { setFlightCardModal({ ticketId: f.point.ticket_id, candidate: f.candidate }) } else { onClickCandidate(f.candidate) } }}>
                          <span style={{ flexShrink: 0 }}>{f.transport_type === 'автобус' ? '🚌' : (f.type === 'dep' ? '✈' : '🛬')}</span>
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>{shortName(f.candidate.name)}</span>
                          {f.point?.is_return && <span style={{ flexShrink: 0, fontSize: 9, fontWeight: 600, padding: '0 4px', borderRadius: 8, background: '#E6F1FB', color: '#0C447C' }}>обратный</span>}
                        </div>
                      ))}
                      {dayFlights.length > 3 && (
                        expandedDay === dateStr ? (
                          <>
                            {dayFlights.slice(3).map((f, i) => (
                              <div key={'extra-' + i} style={{ fontSize: 10, padding: '2px 3px', borderRadius: 3, background: f._isAgent ? (f.type === 'dep' ? '#FEF3E2' : '#F0EAF8') : (f.type === 'dep' ? '#E6F1FB' : '#EAF3DE'), color: f._isAgent ? (f.type === 'dep' ? '#7A4500' : '#4A2080') : (f.type === 'dep' ? '#0C447C' : '#27500A'), cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 2, minWidth: 0, overflow: 'hidden' }}
                                onMouseEnter={e => { const rect = e.currentTarget.getBoundingClientRect(); setTooltip({ flight: f, x: rect.left, y: rect.bottom + 4 }) }}
                                onMouseLeave={() => setTooltip(null)}
                                onClick={() => { if (f._isAgent) { onClickAgent && onClickAgent() } else if (f.point.ticket_id) { setFlightCardModal({ ticketId: f.point.ticket_id, candidate: f.candidate }) } else { onClickCandidate(f.candidate) } }}>
                                <span style={{ flexShrink: 0 }}>{f.transport_type === 'автобус' ? '🚌' : (f.type === 'dep' ? '✈' : '🛬')}</span>
                                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>{shortName(f.candidate.name)}</span>
                                {f.point?.is_return && <span style={{ flexShrink: 0, fontSize: 9, fontWeight: 600, padding: '0 4px', borderRadius: 8, background: '#E6F1FB', color: '#0C447C' }}>обратный</span>}
                              </div>
                            ))}
                            <div onClick={() => setExpandedDay(null)} style={{ fontSize: 9, color: '#378ADD', cursor: 'pointer' }}>Свернуть</div>
                          </>
                        ) : (
                          <div onClick={() => setExpandedDay(dateStr)} style={{ fontSize: 9, color: '#378ADD', cursor: 'pointer' }}>+{dayFlights.length - 3}</div>
                        )
                      )}
                    </div>
                  )}

                  {calTab === 'hotels' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {dayBookings.map((b, i) => (
                        <div key={i} style={{ fontSize: 10, padding: '2px 3px', borderRadius: 3, background: isCheckIn(b, day) ? (b._isAgent ? '#EAF3DE' : '#FBEAF0') : '#f5f5f3', color: isCheckIn(b, day) ? (b._isAgent ? '#27500A' : '#72243E') : '#555', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 2, borderLeft: isCheckIn(b, day) ? ('2px solid ' + (b._isAgent ? '#639922' : '#72243E')) : '2px solid #ddd', minWidth: 0, overflow: 'hidden' }}
                          onMouseEnter={e => { const rect = e.currentTarget.getBoundingClientRect(); setTooltip({ booking: b, x: rect.left, y: rect.bottom + 4 }) }}
                          onMouseLeave={() => setTooltip(null)}
                          onClick={() => setEditBookingModal({ booking: b, candidate: b.candidate })}>
                          <span style={{ flexShrink: 0 }}>{isCheckIn(b, day) ? (b._isAgent ? '🤝' : '🏨') : '—'}</span>
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>{shortName(b.candidate.name)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          )
        })}
      </div>

      {tooltip && tooltip.flight && (
        <div style={{ position: 'fixed', left: Math.min(tooltip.x, window.innerWidth - 260), top: tooltip.y, zIndex: 1000, background: 'white', border: '1px solid #e8e8e5', borderRadius: 10, padding: '12px 14px', width: 250, boxShadow: '0 4px 20px rgba(0,0,0,0.12)', pointerEvents: 'none' }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>{tooltip.flight.candidate.name}</div>
          <div style={{ fontSize: 12, color: '#444', marginBottom: 4 }}>{tooltip.flight.transport_type === 'автобус' ? (tooltip.flight.type === 'dep' ? '🚌 Отправление из' : '🚌 Прибытие в') : (tooltip.flight.type === 'dep' ? '✈ Вылет из' : '🛬 Прилёт в')} <strong>{tooltip.flight.city || '—'}</strong></div>
          {tooltip.flight.type === 'dep' && tooltip.flight.toCity && <div style={{ fontSize: 12, color: '#444', marginBottom: 4 }}>→ {tooltip.flight.toCity}</div>}
          {tooltip.flight.type === 'arr' && tooltip.flight.fromCity && <div style={{ fontSize: 12, color: '#444', marginBottom: 4 }}>из {tooltip.flight.fromCity}</div>}
          {tooltip.flight.time && <div style={{ fontSize: 12, marginBottom: 2 }}><span style={{ color: '#888' }}>Местное: </span>{fmtTime(tooltip.flight.date, tooltip.flight.time)}</div>}
          {tooltip.flight.time && tooltip.flight.tz !== 'Europe/Moscow' && <div style={{ fontSize: 12, marginBottom: 2 }}><span style={{ color: '#888' }}>МСК: </span>{fmtMsk(tooltip.flight.date, tooltip.flight.time, tooltip.flight.tz)}</div>}
          <div style={{ fontSize: 11, color: '#bbb', marginTop: 6 }}>Нажмите чтобы открыть карточку</div>
        </div>
      )}

      {tooltip && tooltip.booking && (
        <div style={{ position: 'fixed', left: Math.min(tooltip.x, window.innerWidth - 260), top: tooltip.y, zIndex: 1000, background: 'white', border: '1px solid #e8e8e5', borderRadius: 10, padding: '12px 14px', width: 250, boxShadow: '0 4px 20px rgba(0,0,0,0.12)', pointerEvents: 'none' }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>{tooltip.booking.candidate.name}</div>
          <div style={{ fontSize: 12, color: '#444', marginBottom: 4 }}>🏨 {tooltip.booking.hotel_name || 'Отель'}</div>
          {tooltip.booking.city && <div style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>📍 {tooltip.booking.city}</div>}
          <div style={{ fontSize: 12, marginBottom: 2 }}><span style={{ color: '#888' }}>Заезд: </span>{tooltip.booking.check_in ? tooltip.booking.check_in.split('-').reverse().join('.') : '—'}</div>
          <div style={{ fontSize: 12, marginBottom: 2 }}><span style={{ color: '#888' }}>Выезд: </span>{tooltip.booking.check_out ? tooltip.booking.check_out.split('-').reverse().join('.') : '—'}</div>
          {tooltip.booking.price && <div style={{ fontSize: 12, marginBottom: 2 }}><span style={{ color: '#888' }}>Стоимость: </span>{parseFloat(tooltip.booking.price).toLocaleString('ru-RU')} ₽</div>}
          {tooltip.booking.notes && <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>{tooltip.booking.notes}</div>}
          <div style={{ fontSize: 11, color: '#bbb', marginTop: 6 }}>Нажмите чтобы открыть карточку</div>
        </div>
      )}
{editingBooking && editForm && (
        <div className="modal-backdrop">
          <div className="modal modal-sm" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>✏ Редактировать бронирование</h2>
              <button className="close-btn" onClick={() => setEditingBooking(false)}>✕</button>
            </div>
            <form className="modal-form" onSubmit={async e => {
              e.preventDefault()
              const bm = editBookingModal
              if (bm.booking._isAgent) {
                const freshA = await fetchAgentById(bm.booking._agentId)
                if (!freshA) return
                const updated = (freshA.hotel_bookings || []).map((b, i) => i === bm.booking._bookingIdx ? { ...b, hotel: editForm.hotel_name, check_in: editForm.check_in, check_out: editForm.check_out, price: editForm.price, notes: editForm.notes } : b)
                await updateAgent(freshA.id, { hotel_bookings: updated })
                setLocalAgents(prev => prev.map(a => a.id === freshA.id ? { ...a, hotel_bookings: updated } : a))
                setEditBookingModal({ ...bm, booking: { ...bm.booking, hotel_name: editForm.hotel_name, check_in: editForm.check_in, check_out: editForm.check_out, price: editForm.price, notes: editForm.notes } })
              } else {
                const freshC = await fetchCandidateById(bm.candidate.id)
                const updatedBookings = (freshC.hotel_bookings || []).map(b => b.created_at === bm.booking.created_at ? { ...editForm } : b)
                await updateCandidate(freshC.id, { hotel_bookings: updatedBookings })
                setLocalCandidates(prev => prev.map(c => c.id === freshC.id ? { ...c, hotel_bookings: updatedBookings } : c))
                setEditBookingModal({ ...bm, booking: editForm })
              }
              setEditingBooking(false)
            }}>
              <label>Название и адрес отеля
                <input value={editForm.hotel_name || ''} onChange={e => setEditForm(f => ({ ...f, hotel_name: e.target.value }))} placeholder="Название отеля, адрес" />
              </label>
              {!editBookingModal?.booking?._isAgent && <label>Локация
                <select value={editForm.location_id || ''} onChange={e => setEditForm(f => ({ ...f, location_id: e.target.value, location_name: locations?.find(l => l.id === e.target.value)?.name || '' }))}>
                  <option value="">— выберите локацию —</option>
                  {locations.map(l => <option key={l.id} value={l.id}>{l.icon} {l.name}</option>)}
                </select>
              </label>}
              <div className="row-2">
                <label>Дата заезда
                  <input type="date" value={editForm.check_in || ''} onChange={e => setEditForm(f => ({ ...f, check_in: e.target.value }))} />
                </label>
                <label>Дата выезда
                  <input type="date" value={editForm.check_out || ''} onChange={e => setEditForm(f => ({ ...f, check_out: e.target.value }))} />
                </label>
              </div>
              <label>Стоимость (₽)
                <input type="number" value={editForm.price || ''} onChange={e => setEditForm(f => ({ ...f, price: e.target.value }))} placeholder="0" />
              </label>
              <label>Примечание
                <input value={editForm.notes || ''} onChange={e => setEditForm(f => ({ ...f, notes: e.target.value }))} placeholder="Номер брони, условия..." />
              </label>
              <div className="modal-actions">
                <button type="button" className="btn btn-outline" onClick={() => setEditingBooking(false)}>Отмена</button>
                <button type="submit" className="btn btn-primary">Сохранить</button>
              </div>
            </form>
          </div>
        </div>
      )}
     {editBookingModal && (
        <div className="modal-backdrop">
          <div className="modal" style={{ maxWidth: 520 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>🏨 Бронирование</h2>
              <button className="close-btn" onClick={() => setEditBookingModal(null)}>✕</button>
            </div>
            <div className="detail-body">
              <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>{editBookingModal.candidate.name}</div>
              {editBookingModal.booking._isAgent && <div style={{ fontSize: 11, color: '#639922', marginBottom: 8 }}>🤝 Агент</div>}
              <table className="detail-table">
                <tbody>
                  <tr><td>Отель</td><td>{editBookingModal.booking.hotel_name || '—'}</td></tr>
                  {!editBookingModal.booking._isAgent && <tr><td>Локация</td><td>{editBookingModal.booking.location_name || '—'}</td></tr>}
                  <tr><td>Заезд</td><td>{editBookingModal.booking.check_in ? editBookingModal.booking.check_in.split('-').reverse().join('.') : '—'}</td></tr>
                  <tr><td>Выезд</td><td>{editBookingModal.booking.check_out ? editBookingModal.booking.check_out.split('-').reverse().join('.') : '—'}</td></tr>
                  {editBookingModal.booking.price > 0 && <tr><td>Стоимость</td><td>{parseFloat(editBookingModal.booking.price).toLocaleString('ru-RU')} ₽</td></tr>}
                  {editBookingModal.booking.notes && <tr><td>Примечание</td><td>{editBookingModal.booking.notes}</td></tr>}
                </tbody>
              </table>
              {editBookingModal.booking.file && (
                <div style={{ marginTop: 10 }}>
                  <a href={editBookingModal.booking.file.url} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: '#185FA5', textDecoration: 'none' }}>📄 {editBookingModal.booking.file.name}</a>
                </div>
              )}
            </div>
<div style={{ height: 1, background: '#f0f0ec', margin: '12px 0' }} />
            <div className="modal-actions">
              {isAdmin && <button className="btn btn-danger" onClick={async () => {
                if (!window.confirm('Удалить бронирование?')) return
                const bm = editBookingModal
                if (bm.booking._isAgent) {
                  const freshA = await fetchAgentById(bm.booking._agentId)
                  if (!freshA) return
                  const updated = (freshA.hotel_bookings || []).filter((_, i) => i !== bm.booking._bookingIdx)
                  await updateAgent(freshA.id, { hotel_bookings: updated })
                  setLocalAgents(prev => prev.map(a => a.id === freshA.id ? { ...a, hotel_bookings: updated } : a))
                } else {
                  const freshC = await fetchCandidateById(bm.candidate.id)
                  const updated = (freshC.hotel_bookings || []).filter(b => b.created_at !== bm.booking.created_at)
                  await updateCandidate(freshC.id, { hotel_bookings: updated })
                  setLocalCandidates(prev => prev.map(c => c.id === freshC.id ? { ...c, hotel_bookings: updated } : c))
                }
                setEditBookingModal(null)
              }}>Удалить</button>}
              {isAdmin && <button className="btn btn-outline" onClick={() => { setEditForm({ ...editBookingModal.booking }); setEditingBooking(true) }}>Редактировать</button>}
              <button className="btn btn-outline" onClick={() => setEditBookingModal(null)}>Закрыть</button>
              <button className="btn btn-primary" onClick={() => {
                const agentId = editBookingModal.booking._agentId
                setEditBookingModal(null)
                if (editBookingModal.booking._isAgent) { onClickAgent && onClickAgent(agentId) }
                else onClickCandidate(editBookingModal.candidate)
              }}>{editBookingModal.booking._isAgent ? 'Открыть карточку агента' : 'Открыть карточку'}</button>
            </div>
          </div>
        </div>
      )} 
{bookingModal && <BookingModal date={bookingModal.date} candidates={localCandidates} locations={locations} onClose={() => setBookingModal(null)} onSave={handleSaveBooking} />}
{flightCardModal && (
        <FlightCardModal
          ticketId={flightCardModal.ticketId}
          candidate={flightCardModal.candidate}
          onClose={() => setFlightCardModal(null)}
          onOpenCandidate={() => { (onClickFromFlight || onClickCandidate)(flightCardModal.candidate, flightCardModal.ticketId) }}
          isAdmin={isAdmin}
        />
      )}
{ticketModal && <TicketModal candidates={localCandidates} locations={locations} currentUserId={currentUserId} currentUserName={currentUserName} onClose={() => setTicketModal(false)} onSaved={() => { setTicketModal(false) }} />}
    </div>
  )
}